/** Angular Core */
import { Injectable } from '@angular/core';
/** Models */
import { Plazo } from '@models/plazo/plazo.interface';
/** Constants */
import { CC } from '@constants/coverages';
/** Services */
import { ProspectoService } from '@services/prospecto/prospecto.service';
import { BAService } from '@services/coverages/BA/ba.service';
import { FactoresService } from '@services/factores/factores.service';
import { RecursosExternosService } from '@services/recursosExternos/recursos-externos.service';
/** Rxjs */
import { BehaviorSubject, Subscription } from 'rxjs';
import { OpcionesPlanService } from '@app/services/opcionesPlan/opciones-plan.service';

/**
 * Servicio de cobertura CC
 * @export
 * @class
 */
@Injectable({
  providedIn: 'root'
})
export class CCService {
  /**
   * Crea una instancia reactiva de la prima anual en la primera moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_UDI = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de la prima anual en la segunda moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_MXN = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de suma asegurada en la primera moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  sumaAsegurada_UDI = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de suma asegurada en la segunda moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  sumaAsegurada_MXN = new BehaviorSubject<number>(0);
  // Prima anual Sugerida 1
  /**
   * Crea una instancia reactiva de la prima anual sugerida 1 en la primera moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_UDI_Sugerida1 = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de la prima anual sugerida 1 en la segunda moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_MXN_Sugerida1 = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de la prima anual sugerida 2 en la primera moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_UDI_Sugerida2 = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de la prima anual sugerida 2 en la segunda moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_MXN_Sugerida2 = new BehaviorSubject<number>(0);

  // Observables
  /**
   * Crea una instancia reactiva como observable de la prima anual en la primera moneda
   * @property
   * @type {Observable<number>}
   * @default 0
   */
  primaAnual_UDI$ = this.primaAnual_UDI.asObservable();
  /**
   * Crea una instancia reactiva como observable de la prima anual en la segunda moneda
   * @property
   * @type {Observable<number>}
   * @default 0
   */
  primaAnual_MXN$ = this.primaAnual_MXN.asObservable();
  /**
   * Crea una instancia reactiva como observable de la suma asegurada en la primera moneda
   * @property
   * @type {Observable<number>}
   * @default 0
   */
  sumaAsegurada_UDI$ = this.sumaAsegurada_UDI.asObservable();
  /**
   * Crea una instancia reactiva como observable de la suma asegurada en la segunda moneda
   * @property
   * @type {Observable<number>}
   * @default 0
   */
  sumaAsegurada_MXN$ = this.sumaAsegurada_MXN.asObservable();
  /**
   * Selección por default
   * @property
   * @default false
   */
  DEFAULT = true;
  /**
   * Crea una suscripción almacenando la suma asegurada de la cobertura Básica
   * @property {Subscription}
   */
  sumaAsegurada_BA: Subscription;

  /**
   * Constructor.
   * @constructor
   * @param recursosExternos Crea instancia de RecursosExternosService
   * @param factores Crea instancia de FactoresService
   * @param BaService Crea instacia de BAService
   * @param prospectoService Crea instancia de PropesctoService
   */
  constructor(
    private recursosExternos: RecursosExternosService,
    private factores: FactoresService,
    private BaService: BAService,
    private prospectoService: ProspectoService,
    private opc: OpcionesPlanService
  ) {
  }
  /**
   * Establece la prima anual en la cobertura dado el valor
   * @param sumaAseguradaUDI
   */
  establecePrimaAnual(sumaAseguradaUDI) {
    this.sumaAsegurada_BA = this.BaService.sumaAsegurada_UDI.distinctUntilChanged()
    .subscribe(sumaAsegurada => {
      this.obtenerPrima(sumaAseguradaUDI);
    });
  }
  /**
   * Obtiene la prima apartir del valor asignado
   * @param {number} sumaAsegurada
   */
  obtenerPrima(sumaAsegurada: number): void {
    // Variable de suma asegurada que contendra el valor final de la suma asegurada despues de ser evaluada.
    let suma = 0;

    /**
     * Si la suma asegurada inicial es igual a 0, tomará por defecto el valor de la suma asegurada basica
     * de lo contrario continuará evaluando la suma asegurada introducida.
    */
    this.DEFAULT = sumaAsegurada === 0;

    // Se obtienen la informacion del prospecto actual
    const prospecto = this.prospectoService.prospecto.value;

    // Evalua si la edad del prospecto esta dentro del rango de aceptación de la cobertura
    if (
      prospecto.age >= CC.ACCEPTANCE_AGE.MIN &&
      prospecto.age <= CC.ACCEPTANCE_AGE.MAX
    ) {
      if (sumaAsegurada !== -1) {
        if (this.DEFAULT) {
          suma = this.BaService.sumaAsegurada_UDI.value;
          this.DEFAULT = false;
        } else {
          suma = sumaAsegurada || 0;
        }

        if (
          suma > this.BaService.sumaAsegurada_UDI.value ||
          suma < this.recursosExternos.GetMinMax('BA', 'MIN')
        ) {
          suma = this.BaService.sumaAsegurada_UDI.value;
        }
        if (this.opc.tipoPlan === 'TEMPORAL' && this.opc.moneda === 'MXN') {
          suma = this.recursosExternos.obtenerSumaAsegurada('PlanTmpCC', suma);
        } else {
          suma = this.recursosExternos.obtenerSumaAsegurada('CC', suma);
        }

        // if (suma <= this.recursosExternos.GetMinMax('CC', 'MIN')) {
        //   suma = this.recursosExternos.GetMinMax('CC', 'MIN');
        // }

        // if (suma >= this.recursosExternos.GetMinMax('CC', 'MAX')) {
        //   suma = this.recursosExternos.GetMinMax('CC', 'MAX');
        // }


        const primaCC_UDI = this.calculoPrimaCC(this.recursosExternos.FirstToUDI(suma), 'Elegido');
        const primaCC_MXN = this.recursosExternos.UDIToSecond(primaCC_UDI);

        const primaCC_UDI_Sugerida1 = this.calculoPrimaCC(this.recursosExternos.FirstToUDI(suma), 'Sugerido1');
        const primaCC_MXN_Sugerida1 = this.recursosExternos.UDIToSecond(primaCC_UDI_Sugerida1);

        const primaCC_UDI_Sugerida2 = this.calculoPrimaCC(this.recursosExternos.FirstToUDI(suma), 'Sugerido2');
        const primaCC_MXN_Sugerida2 = this.recursosExternos.UDIToSecond(primaCC_UDI_Sugerida2);

        const sumaAsegurada_UDI = suma;
        const sumaAsegurada_MXN = this.recursosExternos.FirstToSecond(sumaAsegurada_UDI);

        this.sumaAsegurada_UDI.next(sumaAsegurada_UDI);
        this.sumaAsegurada_MXN.next(sumaAsegurada_MXN);

        this.primaAnual_UDI.next(this.recursosExternos.UDIToFirst(primaCC_UDI));
        this.primaAnual_MXN.next(primaCC_MXN);

        this.primaAnual_UDI_Sugerida1.next(this.recursosExternos.UDIToFirst(primaCC_UDI_Sugerida1));
        this.primaAnual_MXN_Sugerida1.next(primaCC_MXN_Sugerida1);

        this.primaAnual_UDI_Sugerida2.next(this.recursosExternos.UDIToFirst(primaCC_UDI_Sugerida2));
        this.primaAnual_MXN_Sugerida2.next(primaCC_MXN_Sugerida2);


        return;

      }

    }
      this.resetValues();
      return;
  }

  /**
   * Realiza el calculo de la tarifa
   *
   * @param {number} calculoPrimaCC
   * @returns {number} Prima CC
   * @memberof PAIService
   */
  calculoPrimaCC(sumaAsegurada: number, plazo: Plazo, isINPC: boolean = false, edadInpc?: number): number {

    // Variables necesarias para el calculo
    const factorCC = this.factores.obtenerFactorCobertura('CC', plazo, isINPC, edadInpc);

    // Ejecuta la formula dada
    const primaCC = factorCC !== 0 ? (
      sumaAsegurada * factorCC / 1000
    ) : 0;

    // Retorna el resultado de la operación
    return primaCC;
  }

  resetValues(): void {
    this.primaAnual_UDI.next(0);
    this.primaAnual_MXN.next(0);
    this.primaAnual_UDI_Sugerida1.next(0);
    this.primaAnual_MXN_Sugerida1.next(0);
    this.primaAnual_UDI_Sugerida2.next(0);
    this.primaAnual_MXN_Sugerida2.next(0);
    this.sumaAsegurada_UDI.next(0);
    this.sumaAsegurada_MXN.next(0);
    this.DEFAULT = true;
  }

}
